import { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { useSort, SortToggleType } from '@table-library/react-table-library/sort';
import { useTheme as buildTheme } from '@table-library/react-table-library/theme';
import { usePagination } from '@table-library/react-table-library/pagination';
import { useErrorBoundary } from 'react-error-boundary';
import { Pagination } from './Pagination.js';
import { FavoriteDesignation } from './FavoriteDesignation.js';
import FavoriteFilter from './FavoriteFilter.js';
import HcpFilters from './HcpFilters.js';
import Tooltip from './Tooltip.js';
import OpportunityScoreModal from './OpportunityScoreModal.js';
import OppScoreLevelsTooltip from './toolTips/OppScoreLevelsTooltip.js';
import { getConfigValue } from '../util/getConfigValue.js';
import { getSanitizedHtml } from '../util/getSanitizedHtml.js';
import { compareForDynamicTable } from '../util/dynamicTableSort.js';
import { getTableColumnsSpec } from '../util/getTableColumnsSpec.js';
import { hcpTab, OppScore } from '../util/constants.js';
import { containsHtmlTags } from '../util/validation';
import { debounce } from '@mui/material';
import PropTypes from 'prop-types';
import { AppContext } from '../util/AppContext';

const setActiveIconColor = {
	color: 'white',
};

const noBottomBorder = {
	borderBottom: 'none',
};

const THEME = {
	Row: `
        &.row-select-selected,
        &.row-select-single-selected {
        font-weight: normal;
        }
  `,
	Cell: `
        & div 	{
          white-space: normal;
        }
  `,
};

SelectTargetComponent.propTypes = {
	filterFunction: PropTypes.func.isRequired,
	handleChange: PropTypes.func.isRequired,
	activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	showResults: PropTypes.bool.isRequired,
	setShowResults: PropTypes.func.isRequired,
	getPrescribers: PropTypes.func.isRequired,
	getGroupPractices: PropTypes.func.isRequired,
	getGeographyAggregates: PropTypes.func.isRequired,
	handleActive: PropTypes.func.isRequired,
	searchInput: PropTypes.string.isRequired,
	setSearchInput: PropTypes.func.isRequired,
	showMaxRecordsMessage: PropTypes.bool,
	delaySearch: PropTypes.bool,
	useHcpFiltersDropdown: PropTypes.bool,
	showHcpOpportunity: PropTypes.bool,
};

function SelectTargetComponent({
	filterFunction,
	handleChange,
	activeId,
	showResults,
	setShowResults,
	getPrescribers,
	getGroupPractices,
	getGeographyAggregates,
	handleActive,
	searchInput,
	setSearchInput,
	showMaxRecordsMessage = false,
	delaySearch = false,
	useHcpFiltersDropdown = false,
	showHcpOpportunity = true
}) {
	const [appState, setAppState] = useContext(AppContext);
	const [prescribers, setPrescribers] = useState([]);
	const [prescriberHeaders, setPrescriberHeaders] = useState([]);
	const [groupPractices, setGroupPractices] = useState([]);
	const [groupPracticeHeaders, setGroupPracticeHeaders] = useState([]);
	const [geographyAggregates, setGeographyAggregates] = useState([]);
	const [geographyAggregateHeaders, setGeographyAggregateHeaders] = useState([]);
	const [headers, setHeaders] = useState([]);
	const [data, setData] = useState({ nodes: [] });
	const [previousSearchInput, setPreviousSearchInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [numberOfPages, setNumberOfPages] = useState(0);
	const [themeColumns, setThemeColumns] = useState(null);
	const [prescribersLoaded, setPrescribersLoaded] = useState(false);
	const [groupPracticesLoaded, setGroupPracticesLoaded] = useState(false);
	const [geographyAggregatesLoaded, setGeographyAggregatesLoaded] = useState(false);
	const [activeTab, setActiveTab] = useState('0'); // tabs: 1, 2, 3
	const [showGroupPracticeTab, setShowGroupPracticeTab] = useState(false);
	const [showPrescriberTab, setShowPrescriberTab] = useState(false);
	const [showGeographyAggregateTab, setShowGeographyAggregateTab] = useState(false);
	const [maxRecordsMessage, setMaxRecordsMessage] = useState();
	const [validationError, setValidationError] = useState(null);
	const canDisplayRecentWinIndicator = getConfigValue(appState?.config?.Target?.List?.DisplayRecentWins?.Enabled, appState)?.Value === 'true';

	const containsHtmlMessage = getConfigValue(appState?.config?.Common?.Validation?.Message?.ContainsHtml, appState)?.Value || null;
	let maxRecords = 100;
	if (getConfigValue(appState?.config?.Target?.List?.MaxRecordsToDisplay, appState)?.Value) {
		maxRecords = parseInt(getConfigValue(appState?.config?.Target?.List?.MaxRecordsToDisplay, appState)?.Value, 10);
	}

	const groupPracticeEnabled = useMemo(() => getConfigValue(appState.config.GroupPractice?.Enabled, appState)?.Value === 'true', [appState]);
	const geographyAggregateEnabled = useMemo(() => getConfigValue(appState.config.GeographyAggregate?.Enabled, appState)?.Value === 'true', [appState]);

	const hcpFavoritesEnabled = getConfigValue(appState.config.HCP?.Favorites?.Enabled, appState)?.Value === 'true';
	const preCallPlanningEnabled = getConfigValue(appState.config?.PreCall?.Planning?.Enabled, appState)?.Value === 'true';
	const hcpOpportunityEnabled = showHcpOpportunity && getConfigValue(appState.config?.HCP?.Opportunity?.Enabled, appState)?.Value === 'true';
	const rankHeaderTitle = getConfigValue(appState.config?.Target?.SelectHcp?.Rank?.Label, appState)?.Value || 'Rank';
	const oppScoreHeaderTitle = getConfigValue(appState.config?.Target?.OppScore?.Label, appState)?.Value || 'Opp. Score';
	const oppScoreHighLabel = getConfigValue(appState.config?.Target?.High?.Label, appState)?.Value || 'High';
	const oppScoreMediumLabel = getConfigValue(appState.config?.Target?.Medium?.Label, appState)?.Value || 'Medium';
	const oppScoreLowLabel = getConfigValue(appState.config?.Target?.Low?.Label, appState)?.Value || 'Low';
	const rankHeaderTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.Rank?.ToolTip?.Label, appState)?.Value;
	const oppScoreTooltipTitle = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.ToolTip?.Title, appState)?.Value || 'Opportunity Score Levels:';
	const oppScoreHighDescription = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.ToolTip?.High?.Description, appState)?.Value || 'Model-estimated opportunity is high for this HCP based on available claims/transactions and access factors.';
	const oppScoreMediumDescription = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.ToolTip?.Medium?.Description, appState)?.Value || 'Model-estimated opportunity is medium for this HCP based on available claims/transactions and access factors.';
	const oppScoreLowDescription = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.ToolTip?.Low?.Description, appState)?.Value || 'Model-estimated opportunity is low for this HCP based on available claims/transactions and access factors.'
	const rankTooltipTarget = useRef(null);
	const [showRankTooltip, setShowRankTooltip] = useState(false);
	const oppScoreHeaderTooltipTarget = useRef(null);
	const [showOppScoreHeaderTooltip, setShowOppScoreHeaderTooltip] = useState(false);

	const oppScoreLevelsTooltipComponent = useMemo(() => (
		<OppScoreLevelsTooltip
			title={oppScoreTooltipTitle}
			highLabel={oppScoreHighLabel}
			mediumLabel={oppScoreMediumLabel}
			lowLabel={oppScoreLowLabel}
			highDescription={oppScoreHighDescription}
			mediumDescription={oppScoreMediumDescription}
			lowDescription={oppScoreLowDescription}
		/>
	), [oppScoreTooltipTitle, oppScoreHighLabel, oppScoreMediumLabel, oppScoreLowLabel, oppScoreHighDescription, oppScoreMediumDescription, oppScoreLowDescription]);
	const [showOpportunityScoreModal, setShowOpportunityScoreModal] = useState(false);
	const [selectedOpportunityRow, setSelectedOpportunityRow] = useState(null);
	const shouldUseHcpFiltersDropdown = useHcpFiltersDropdown && (preCallPlanningEnabled || hcpOpportunityEnabled);
	const [hcpFavoriteCheckbox, setHcpFavoriteCheckbox] = useState(getConfigValue(appState.config.HCP?.Favorites?.Filter?.Default, appState)?.Value === 'true');
	const [previousHcpFavoriteCheckbox, setPreviousHcpFavoriteCheckbox] = useState(
		getConfigValue(appState.config.HCP?.Favorites?.Filter?.Default, appState)?.Value === 'true',
	);
	const [highOpportunityCheckbox, setHighOpportunityCheckbox] = useState(false);
	const [previousHighOpportunityCheckbox, setPreviousHighOpportunityCheckbox] = useState(false);

	const featureFlag_hideTabsWhenNoData = getConfigValue(appState.config.CreateSalesAid?.Features?.HideTabsWhenNoData, appState)?.Value === 'true';

	const { showBoundary } = useErrorBoundary();

	const pagination = usePagination(data, {
		state: {
			page: 0,
			size: appState.config?.Target?.RecordsPerPage?.Value,
		},
	});

	const executeFilter = async (data, search, favoriteOnly, activeTab) => {
		filterFunction(data, search, favoriteOnly, activeTab)
			.then((filteredData) => {
				const dataAfterOpportunityFilter = highOpportunityCheckbox
					? filteredData.filter((row) => Number(row?.data?.opportunityScore) === OppScore.Score.High)
					: filteredData;

				let length = dataAfterOpportunityFilter.length;
				setData({ nodes: dataAfterOpportunityFilter });
				setShowResults(length > 0);

				if (
					searchInput !== previousSearchInput ||
					hcpFavoriteCheckbox !== previousHcpFavoriteCheckbox ||
					highOpportunityCheckbox !== previousHighOpportunityCheckbox
				) {
					pagination.fns.onSetPage(0);
					setPreviousSearchInput(searchInput);
					setPreviousHcpFavoriteCheckbox(hcpFavoriteCheckbox);
					setPreviousHighOpportunityCheckbox(highOpportunityCheckbox);
				}

				const pageSize = parseInt(appState.config?.Target?.RecordsPerPage?.Value, 10);
				let pages = Math.floor(length / pageSize);
				pages = length % pageSize > 0 ? pages + 1 : pages;
				setNumberOfPages(pages);
				if (pagination.state.page >= pages) {
					pagination.fns.onSetPage(pages === 0 ? 0 : pages - 1);
				}

				if (showMaxRecordsMessage && length === maxRecords) {
					setMaxRecordsMessage(getConfigValue(appState?.config?.Target?.List?.MaxRecordsMessage, appState)?.Value);
				} else {
					setMaxRecordsMessage(null);
				}
				setIsLoading(false);
			})
			.catch((error) => {
				showBoundary(error);
			});
	};

	//Define debounced search function so the API is not called on every keystroke but only after a delay.
	const debouncedSearch = useRef(
		debounce((data, filter, favoriteOnly, activeTab) => {
			executeFilter(data, filter, favoriteOnly, activeTab);
		}, 300),
	).current;

	function getActiveTabData() {
		let data = [];
		let headers = [];
		if (activeTab === hcpTab.Prescriber) {
			data = prescribers;
			headers = prescriberHeaders;
		} else if (activeTab === hcpTab.GroupPractice) {
			data = groupPractices;
			headers = groupPracticeHeaders;
		} else if (activeTab === hcpTab.GeographyAggregate) {
			data = geographyAggregates;
			headers = geographyAggregateHeaders;
		}

		if (hcpOpportunityEnabled) {
			const rankHeader = { key: 'rank', title: rankHeaderTitle, sortField: 'rank' };
			const opportunityScoreHeader = { key: 'opportunityScore', title: oppScoreHeaderTitle, sortField: 'opportunityScore' };
			const hasRankData = data.some((row) => row?.data?.rank !== undefined && row?.data?.rank !== null);
			const hasOpportunityScoreData = data.some((row) => row?.data?.opportunityScore !== undefined && row?.data?.opportunityScore !== null);
			if (!hasRankData) {
				return { headers, data };
			}

			const hasRankHeader = headers.some((header) => header.key === 'rank');
			const hasOppScoreHeader = headers.some((header) => header.key === 'opportunityScore');
			let headersWithRank = hasRankHeader ? headers : [rankHeader, ...headers];
			const headersWithOppScore = hasOpportunityScoreData && !hasOppScoreHeader ? [...headersWithRank, opportunityScoreHeader] : headersWithRank.filter((header) => header.key !== 'opportunityScore' || hasOpportunityScoreData);

			const dataWithRank = data.map((row) => {
				const rowColumns = row.columns || {};
				const rawRank = row?.data?.rank;
				const parsedRank = rawRank !== undefined && rawRank !== null ? Number(rawRank) : null;
				const normalizedRank = parsedRank !== null && !Number.isNaN(parsedRank) ? parsedRank : rawRank;

				const oppScore = row?.data?.opportunityScore;
				const oppScoreDisplay = getOpportunityScoreDisplay(oppScore);

				return {
					...row,
					data: {
						...row.data,
						rank: normalizedRank,
						opportunityScore: oppScore,
					},
					columns: hasOpportunityScoreData
						? {
								rank: normalizedRank !== undefined && normalizedRank !== null ? normalizedRank : '',
								...rowColumns,
								opportunityScore: oppScoreDisplay,
						  }
						: {
								rank: normalizedRank !== undefined && normalizedRank !== null ? normalizedRank : '',
								...rowColumns,
						  },
				};
			});

			return { headers: headersWithOppScore, data: dataWithRank };
		}

		return { headers, data };
	}

	useEffect(() => {
		if (activeTab === '0') {
			setIsLoading(true);
			getPrescribers(searchInput, hcpFavoriteCheckbox)
				.catch((error) => {
					showBoundary(error);
				})
				.then((results) => {
					if (results?.rows?.length > 0) {
						setPrescribers(results.rows);
						setPrescriberHeaders(results.headers);
						setShowPrescriberTab(true);
					}
				})
				.finally(() => {
					setPrescribersLoaded(true);
				});

			if (groupPracticeEnabled) {
				getGroupPractices(searchInput, hcpFavoriteCheckbox)
					.catch((error) => {
						showBoundary(error);
					})
					.then((results) => {
						if (results?.rows?.length > 0) {
							setGroupPractices(results.rows);
							setGroupPracticeHeaders(results.headers);
							setShowGroupPracticeTab(true);
						}
					})
					.finally(() => {
						setGroupPracticesLoaded(true);
					});
			}

			if (geographyAggregateEnabled) {
				getGeographyAggregates(searchInput, hcpFavoriteCheckbox)
					.catch((error) => {
						showBoundary(error);
					})
					.then((results) => {
						if (results?.rows?.length > 0) {
							setGeographyAggregates(results.rows);
							setGeographyAggregateHeaders(results.headers);
							setShowGeographyAggregateTab(true);
						}
					})
					.finally(() => {
						setGeographyAggregatesLoaded(true);
					});
			}

			if (!featureFlag_hideTabsWhenNoData) {
				setShowPrescriberTab(true);
				setShowGroupPracticeTab(groupPracticeEnabled);
				setShowGeographyAggregateTab(geographyAggregateEnabled);
			}
		}

		setSearchInput('');
		pagination.fns.onSetPage(0);
	}, [appState?.market, appState?.territory, appState?.currentProduct?.clientProductId, activeTab, groupPracticeEnabled, geographyAggregateEnabled]);

	useEffect(() => {
		const groupPracticeCheck = !groupPracticeEnabled || groupPracticesLoaded;
		const geographyAggregateCheck = !geographyAggregateEnabled || geographyAggregatesLoaded;
		if (prescribersLoaded && groupPracticeCheck && geographyAggregateCheck && activeTab === '0') {
			if (showPrescriberTab) {
				setActiveTab(hcpTab.Prescriber);
			} else if (showGroupPracticeTab) {
				setActiveTab(hcpTab.GroupPractice);
			} else if (showGeographyAggregateTab) {
				setActiveTab(hcpTab.GeographyAggregate);
			}
		}
		if (prescribersLoaded && groupPracticeCheck && geographyAggregateCheck) {
			setIsLoading(false);
		}
	}, [
		showGroupPracticeTab,
		showPrescriberTab,
		showGeographyAggregateTab,
		prescribersLoaded,
		groupPracticesLoaded,
		groupPracticeEnabled,
		geographyAggregatesLoaded,
		geographyAggregateEnabled,
	]);

	useEffect(() => {
		setIsLoading(true);
		const activeData = getActiveTabData();
		setHeaders(activeData.headers);
		if (delaySearch) {
			debouncedSearch(activeData.data, searchInput, hcpFavoritesEnabled && hcpFavoriteCheckbox, activeTab);
		} else {
			executeFilter(activeData.data, searchInput, hcpFavoritesEnabled && hcpFavoriteCheckbox, activeTab);
		}
	}, [searchInput, prescribers, groupPractices, geographyAggregates, activeTab, hcpFavoriteCheckbox, highOpportunityCheckbox, delaySearch]);

	//build sort functions object
	const sortFunctions = {};
	for (const header of headers) {
		sortFunctions[header.key] = (array) => array.sort((a, b) => compareForDynamicTable(header.key, header.sortField, false, a, b));
	}
	let defaultSortKey = 'Name';
	if (headers && headers.length > 0) {
		defaultSortKey = headers[0].key;
	}

	const sort = useSort(
		data,
		{
			state: {
				sortKey: defaultSortKey,
				reverse: false,
			},
		},
		{
			sortFns: sortFunctions,
			sortToggleType: SortToggleType.AlternateWithReset,
		},
	);

	useEffect(() => {
		const columnsSpec = getTableColumnsSpec(headers);
		const themeObject = buildTheme({
			...THEME,
			Table: `
            --data-table-library_grid-template-columns: 10% ${columnsSpec};
        `,
		});
		setThemeColumns(themeObject);
	}, [headers]);

	const getIcon = (sortKey) => {
		if (sort.state.sortKey === sortKey && sort.state.reverse) {
			return <span className='fa-solid fa-arrow-down-wide-short' style={setActiveIconColor}></span>;
		}

		if (sort.state.sortKey === sortKey && !sort.state.reverse) {
			return <span className='fa-solid fa-arrow-up-short-wide' style={setActiveIconColor}></span>;
		}

		return <span className='fa-solid fa-sort' />;
	};

	const isRankHeader = (header) => {
		const key = String(header?.key || '').toLowerCase();
		const title = String(header?.title || '').toLowerCase();
		const configuredRankTitle = String(rankHeaderTitle || '').toLowerCase();
		return key === 'rank' || (configuredRankTitle && title === configuredRankTitle);
	};

	const isOppScoreHeader = (header) => {
		const key = String(header?.key || '').toLowerCase();
		const title = String(header?.title || '').toLowerCase();
		const configuredOppScoreTitle = String(oppScoreHeaderTitle || '').toLowerCase();
		return key === 'opportunityscore' || (configuredOppScoreTitle && title === configuredOppScoreTitle);
	};

	const getOpportunityScoreDisplay = (score) => {
		const normalizedScore = score !== undefined && score !== null ? Number(score) : null;

		if (normalizedScore === OppScore.Score.High) {
			return oppScoreHighLabel;
		}

		if (normalizedScore === OppScore.Score.Medium) {
			return oppScoreMediumLabel;
		}

		if (normalizedScore === 0 || normalizedScore === OppScore.Score.Low) {
			return oppScoreLowLabel;
		}

		return '';
	};

	const getOpportunityScoreClassName = (score) => {
		const normalizedScore = score !== undefined && score !== null ? Number(score) : null;

		if (normalizedScore === OppScore.Score.High) {
			return OppScore.ClassName.High;
		}

		if (normalizedScore === OppScore.Score.Medium) {
			return OppScore.ClassName.Medium;
		}

		if (normalizedScore === 0 || normalizedScore === OppScore.Score.Low) {
			return OppScore.ClassName.Low;
		}

		return OppScore.ClassName.Base;
	};

	const hasHighOpportunityDataForCurrentTab = useMemo(() => {
		let activeTabRows = [];

		if (activeTab === hcpTab.Prescriber) {
			activeTabRows = prescribers;
		} else if (activeTab === hcpTab.GroupPractice) {
			activeTabRows = groupPractices;
		} else if (activeTab === hcpTab.GeographyAggregate) {
			activeTabRows = geographyAggregates;
		}

		return activeTabRows.some((row) => Number(row?.data?.opportunityScore) === OppScore.Score.High);
	}, [activeTab, prescribers, groupPractices, geographyAggregates]);

	const shouldShowHighOpportunityFilter = hcpOpportunityEnabled && hasHighOpportunityDataForCurrentTab;

	const isRankColumnVisible = headers.some((header) => isRankHeader(header));
	const isOppScoreColumnVisible = headers.some((header) => isOppScoreHeader(header));

	const formatOpportunityMetricValue = (value) => {
		if (value === undefined || value === null || value === '') {
			return '-';
		}

		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			return Math.round(numericValue).toLocaleString();
		}

		return value;
	};

	const openOpportunityScoreModal = (event, rowData) => {
		event.stopPropagation();
		setSelectedOpportunityRow(rowData);
		setShowOpportunityScoreModal(true);
	};

	const closeOpportunityScoreModal = () => {
		setShowOpportunityScoreModal(false);
		setSelectedOpportunityRow(null);
	};

	const renderCellContent = (key, item) => {
		if (key === 'rank') {
			return (
				<span className='circle-singleline circle-singleline-rank'>
					{item.columns[key]}
				</span>
			);
		}

		if (key === 'opportunityScore') {
			return (
				<button
					type='button'
					className={getOpportunityScoreClassName(item.data.opportunityScore) + ' btn-opportunity-score'}
					onClick={(event) => openOpportunityScoreModal(event, item.data)}
				>
					{item.columns[key]}
				</button>
			);
		}

		return (
			<span
				className='form-check-label'
				htmlFor={'selectHCP' + item.data.id}
				dangerouslySetInnerHTML={getSanitizedHtml(item.columns[key])}
			></span>
		);
	};

	useEffect(() => {
		if (!isRankColumnVisible && showRankTooltip) {
			setShowRankTooltip(false);
		}
	}, [isRankColumnVisible, showRankTooltip]);

	useEffect(() => {
		if (!isOppScoreColumnVisible && showOppScoreHeaderTooltip) {
			setShowOppScoreHeaderTooltip(false);
		}
	}, [isOppScoreColumnVisible, showOppScoreHeaderTooltip]);

	useEffect(() => {
		if (!shouldShowHighOpportunityFilter && highOpportunityCheckbox) {
			setHighOpportunityCheckbox(false);
		}
	}, [shouldShowHighOpportunityFilter, highOpportunityCheckbox]);

	function checkIdMatch(id) {
		return id === activeId;
	}

	const updateHcps = function (prevState, hcp) {
		return prevState.map((p) => {
			if (p.data.id === hcp.id) {
				return { ...p, data: { ...p.data, favorite: !hcp.favorite } };
			}
			return p;
		});
	};

	function handleFavoriteClick(hcp) {
		if (activeTab === hcpTab.Prescriber) {
			setPrescribers((prevState) => {
				return updateHcps(prevState, hcp);
			});
		} else if (activeTab === hcpTab.GroupPractice) {
			setGroupPractices((prevState) => {
				return updateHcps(prevState, hcp);
			});
		} else if (activeTab === hcpTab.GeographyAggregate) {
			setGeographyAggregates((prevState) => {
				return updateHcps(prevState, hcp);
			});
		}

		if (appState?.prescriber?.id === hcp.id) {
			setAppState({
				...appState,
				prescriber: { ...appState.prescriber, favorite: !hcp.favorite },
			});
		}
	}

	const handleChangeLocal = (event) => {
		const search = event.target.value.trim();
		if (containsHtmlTags(search)) {
			setValidationError(containsHtmlMessage);
			return;
		}
		setValidationError(null);
		handleChange(event);
	};

	return (
    <div className="card pd-card">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="nav tabs tabs-stretched mt-3" role="tablist">
              {showPrescriberTab && (
                <div
                  className="nav-item"
                >
                  <button
                    className={`nav-link ${activeTab === hcpTab.Prescriber ? "active" : ""}`}
                    id="tab-group"
                    data-bs-toggle="tab"
                    data-bs-target="#tab-hcp-pane"
                    type="button"
                    role="tab"
                    aria-controls="tab-hcp-pane"
                    aria-selected="true"
                    onClick={() => setActiveTab(hcpTab.Prescriber)}
                  >
                    <div className="fa-solid fa-user-doctor tab-icon"></div>{" "}
                    <span className="d-none d-md-inline">
                      {
                        getConfigValue(
                          appState.config?.Target?.SelectHCP?.Title,
                          appState,
                        )?.Value
                      }
                    </span>
                  </button>
                </div>
              )}
              {showGroupPracticeTab && (
                <div
                  className="nav-item"
                >
                  <button
                    className={`nav-link ${activeTab === hcpTab.GroupPractice ? "active" : ""}`}
                    id="tab-group"
                    data-bs-toggle="tab"
                    data-bs-target="#tab-group-pane"
                    type="button"
                    role="tab"
                    aria-controls="tab-group-pane"
                    aria-selected="true"
                    onClick={() => setActiveTab(hcpTab.GroupPractice)}
                  >
                    <div className="fa-solid fa-hospital tab-icon"></div>{" "}
                    <span className="d-none d-md-inline">
                      {
                        getConfigValue(
                          appState.config?.Target?.SelectGroupPractice?.Title,
                          appState,
                        )?.Value
                      }{" "}
                    </span>
                  </button>
                </div>
              )}
              {showGeographyAggregateTab && (
                <div
                  className="nav-item"
                >
                  <button
                    className={`nav-link ${activeTab === hcpTab.GeographyAggregate ? "active" : ""}`}
                    id="tab-geography-aggregate"
                    data-bs-toggle="tab"
                    data-bs-target="#tab-geography-aggregate-pane"
                    type="button"
                    role="tab"
                    aria-controls="tab-geography-aggregate-pane"
                    aria-selected="true"
                    onClick={() => setActiveTab(hcpTab.GeographyAggregate)}
                  >
                    <div className="fa-solid fa-location-dot tab-icon"></div>{" "}
                    <span className="d-none d-md-inline">
                      {
                        getConfigValue(
                          appState.config?.Target?.SelectGeographyAggregate
                            ?.Title,
                          appState,
                        )?.Value
                      }{" "}
                    </span>
                  </button>
                </div>
              )}
            </div>

            {shouldUseHcpFiltersDropdown ? (
              <HcpFilters
                hcpFavoriteCheckbox={hcpFavoriteCheckbox}
                setHcpFavoriteCheckbox={setHcpFavoriteCheckbox}
								highOpportunityCheckbox={highOpportunityCheckbox}
								setHighOpportunityCheckbox={setHighOpportunityCheckbox}
								hcpOpportunityEnabled={hcpOpportunityEnabled}
								showHighOpportunityFilter={shouldShowHighOpportunityFilter}
              />
            ) : (
              <FavoriteFilter
                hcpFavoriteCheckbox={hcpFavoriteCheckbox}
                setHcpFavoriteCheckbox={setHcpFavoriteCheckbox}
                includeWrappingRow={true}
              />
            )}

            {showMaxRecordsMessage && maxRecordsMessage && (
              <div>
                <div className="col-12 p-0">
                  <div className="alert alert-primary" role="alert">
                    <div className="alert-content">
                      <p>{maxRecordsMessage}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SEARCH FIELD */}
            <div className="row my-4">
              <div className="col-12">
                <div className="input-group">
                  <input
                    className="form-control icon-search"
                    type="text"
                    placeholder="Search"
                    value={searchInput}
                    onChange={handleChangeLocal}
                  ></input>
                  {validationError && (
                    <div className="invalid-feedback d-block">
                      {validationError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!isLoading && showResults && headers.length > 0 && (
              <Table
                className="table pd-table pod-table-hcp"
                key={headers.length}
                data={data}
                sort={sort}
                theme={themeColumns}
                layout={{ custom: true, isDiv: true }}
                pagination={pagination}
              >
                {(tableList) => (
                  <>
                    <Header>
                      <HeaderRow className="table-dark d-none d-md-contents">
                        <HeaderCell
                          className="px-2 py-2"
                          scope="col"
                        ></HeaderCell>
                        {headers.map((header) => (
                          <HeaderCell
                            key={header.key}
                            scope="col"
                            className={
                              isRankHeader(header) ? "text-center" : ""
                            }
                            onClick={() => sort.fns.onToggleSort({ sortKey: header.key })}
                            style={{ cursor: 'pointer' }}
                          >
                            {isRankColumnVisible && isRankHeader(header) && (
                              <>
                                <button
                                  type="button"
                                  className="btn-reset me-2"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseOver={() => setShowRankTooltip(true)}
                                  onMouseOut={() => setShowRankTooltip(false)}
                                  onFocus={() => setShowRankTooltip(true)}
                                  onBlur={() => setShowRankTooltip(false)}
                                >
                                  <span
                                    ref={rankTooltipTarget}
                                    className="far fa-circle-question type-16"
                                    style={{ color: "white" }}
                                  ></span>
                                </button>
                                <Tooltip
                                  text={rankHeaderTooltipText}
                                  placement="bottom"
                                  ref={rankTooltipTarget}
                                  showTooltip={showRankTooltip}
                                  index="rank-header-tooltip"
                                  tooltipStyle={{ maxWidth: "420px" }}
                                />
                              </>
                            )}
                            {isOppScoreHeader(header) && (
                              <>
                                <button
                                  type="button"
                                  className="btn-reset me-2"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseOver={() =>
                                    setShowOppScoreHeaderTooltip(true)
                                  }
                                  onMouseOut={() =>
                                    setShowOppScoreHeaderTooltip(false)
                                  }
                                  onFocus={() =>
                                    setShowOppScoreHeaderTooltip(true)
                                  }
                                  onBlur={() =>
                                    setShowOppScoreHeaderTooltip(false)
                                  }
                                >
                                  <span
                                    ref={oppScoreHeaderTooltipTarget}
                                    className="far fa-circle-question type-16"
                                    style={{ color: "white" }}
                                  ></span>
                                </button>
                                <Tooltip
                                  customComponent={
                                    oppScoreLevelsTooltipComponent
                                  }
                                  placement="bottom"
                                  ref={oppScoreHeaderTooltipTarget}
                                  showTooltip={showOppScoreHeaderTooltip}
                                  index="opp-score-header-tooltip"
                                  tooltipStyle={{
                                    maxWidth: "420px",
                                    "--bs-tooltip-opacity": "1",
                                  }}
                                />
                              </>
                            )}
                            {header.title}
                            <button
                              className="btn-ghost btn-table"
                              onClick={(e) => {
                                e.stopPropagation();
                                sort.fns.onToggleSort({ sortKey: header.key });
                              }}
                            >
                              {getIcon(header.key)}
                            </button>
                          </HeaderCell>
                        ))}
                      </HeaderRow>
                    </Header>
                    <Body>
                      {tableList.map((item) => (
                        <Row
                          key={item.data.id}
                          item={item}
                          onClick={() => {
                            if (!hcpFavoritesEnabled) {
                              handleActive(item.data, activeTab);
                            }
                          }}
                          className={
                            activeId === item.data.id ? "table-active" : ""
                          }
                        >
                          <Cell
                            className={
                              canDisplayRecentWinIndicator === true &&
                              item.data.hasWinPlans === true
                                ? "pd-plan-table-row pd-plan-table-indicator-success"
                                : ""
                            }
                            style={noBottomBorder}
                          >
                            <div className="input-container">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="selectHCP"
                                id={"selectHCP" + item.data.id}
                                checked={checkIdMatch(item.data.id)}
                                onChange={() => {
                                  handleActive(item.data, activeTab);
                                }}
                              ></input>
                              <FavoriteDesignation
                                hcp={item.data}
                                updateCallback={() =>
                                  handleFavoriteClick(item.data)
                                }
                              />
                            </div>
                          </Cell>
                          {headers.map((header) => {
                            const key = header.key;

                            return (
                              <Cell
                                key={key}
                                className={key === "rank" ? "text-center" : ""}
                              >
                                {
                                  <span className="d-block d-md-none color-grey-d1">
                                    {item.columns[key] ? header.title : ""}
                                  </span>
                                }
                                {renderCellContent(key, item)}
                              </Cell>
                            );
                          })}
                        </Row>
                      ))}
                    </Body>
                  </>
                )}
              </Table>
            )}

            {!isLoading && !showResults && (
              <div className="color-grey-d1 pb-4">
                {
                  getConfigValue(
                    appState.config?.Common?.NoResultsFound,
                    appState,
                  )?.Value
                }
              </div>
            )}
            {isLoading && (
              <div className="color-grey-d1 pb-4">
                {
                  getConfigValue(appState.config.Common?.Loading, appState)
                    ?.Value
                }
              </div>
            )}
          </div>
        </div>
      </div>

      {!isLoading && numberOfPages > 1 && pagination && (
        <Pagination
          numberOfPages={pagination.state.getTotalPages(data.nodes)}
          currentPage={pagination.state.page}
          setCurrentPage={(index) => {
            pagination.fns.onSetPage(index);
          }}
        />
      )}

      <OpportunityScoreModal
        show={showOpportunityScoreModal}
        onHide={closeOpportunityScoreModal}
        selectedOpportunityRow={selectedOpportunityRow}
        appState={appState}
        getOpportunityScoreClassName={getOpportunityScoreClassName}
        getOpportunityScoreDisplay={getOpportunityScoreDisplay}
        formatOpportunityMetricValue={formatOpportunityMetricValue}
      />
    </div>
  );
}

export default SelectTargetComponent;
